import CloseIcon from '@mui/icons-material/Close';
import { Modal, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { AnnouncementDotIndicator } from '@/components/atoms/AnnouncementDotIndicator';
import { AnnouncementStep } from '@/components/atoms/AnnouncementStep';
import { useAnnouncements } from '@/hooks/useAnnouncements';
import { useTranslator } from '@/hooks/useTranslator';

interface PendingStep {
  readonly next: number;
  readonly direction: 'left' | 'right';
}

export const AnnouncementModal: React.FC = (): React.ReactElement | null => {
  const { announcement, visible, acknowledging, error, acknowledge, dismiss } = useAnnouncements();
  const tr = useTranslator();

  const [currentStep, setCurrentStep] = React.useState(0);
  const [slideDirection, setSlideDirection] = React.useState<'left' | 'right' | null>(null);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [pendingStep, setPendingStep] = React.useState<PendingStep | null>(null);

  React.useEffect((): void => setCurrentStep(0), [announcement?.id]);

  React.useEffect((): (() => void) | undefined => {
    if (!pendingStep) {
      return;
    }

    setSlideDirection(pendingStep.direction);
    setIsAnimating(true);

    const timer: ReturnType<typeof setTimeout> = setTimeout((): void => {
      setCurrentStep(pendingStep.next);
      setSlideDirection(null);
      setIsAnimating(false);
      setPendingStep(null);
    }, 300);

    return (): void => clearTimeout(timer);
  }, [pendingStep]);

  if (!announcement || !visible) {
    return null;
  }

  const steps = announcement.steps;
  const totalSteps = steps.length;
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;
  const isSingleStep = totalSteps === 1;

  const goToStep = (next: number, direction: 'left' | 'right'): void => {
    if (isAnimating) {
      return;
    }
    setPendingStep({ next, direction });
  };

  const handleNext = (): void => {
    if (isLastStep) {
      acknowledge();
    } else {
      goToStep(currentStep + 1, 'left');
    }
  };

  const handleBack = (): void => {
    goToStep(currentStep - 1, 'right');
  };

  const slideStyle: SystemStyleObject<Theme> =
    slideDirection === 'left'
      ? styles.slideLeft
      : slideDirection === 'right'
        ? styles.slideRight
        : styles.slideCenter;

  return (
    <Modal
      open={visible}
      aria-label={tr('ANNOUNCEMENT_FEATURE')}
      onClose={(_: unknown, reason: string): void => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}>
      <Box sx={styles.container}>
        <Paper sx={styles.paper}>
          <IconButton
            onClick={dismiss}
            aria-label={tr('ANNOUNCEMENT_CLOSE')}
            sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>

          <Box sx={styles.contentBox}>
            <Box sx={slideStyle}>
              <AnnouncementStep step={steps[currentStep]} />
            </Box>

            {!isSingleStep && (
              <AnnouncementDotIndicator totalSteps={totalSteps} currentStep={currentStep} />
            )}

            <Box sx={styles.buttonRow}>
              {!isFirstStep && !isSingleStep && (
                <Button
                  variant="outlined"
                  size="compact"
                  onClick={handleBack}
                  aria-label={tr('ANNOUNCEMENT_PREVIOUS_STEP')}
                  disabled={isAnimating}>
                  {tr('ANNOUNCEMENT_BACK')}
                </Button>
              )}
              <Button
                variant="contained"
                size="compact"
                onClick={handleNext}
                disabled={acknowledging || isAnimating}
                aria-label={
                  isLastStep
                    ? tr('ANNOUNCEMENT_ACKNOWLEDGE_AND_CLOSE')
                    : tr('ANNOUNCEMENT_NEXT_STEP')
                }>
                {isLastStep ? tr('ANNOUNCEMENT_GOT_IT') : tr('ANNOUNCEMENT_NEXT')}
              </Button>
            </Box>

            {error && (
              <Typography variant="caption" color="error" sx={styles.errorText}>
                {tr('ANNOUNCEMENT_ACKNOWLEDGE_ERROR')}
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

const styles: Record<string, SystemStyleObject<Theme>> = {
  container: {
    position: 'absolute',
    paddingTop: { sm: 0, xs: 4 },
    top: { sm: 100, xs: 'auto' },
    left: { sm: '50%', xs: 0 },
    bottom: { sm: 'auto', xs: 0 },
    maxWidth: { sm: 'min-content', xs: '100%' },
    transform: { sm: 'translate(-50%, 0)' },
    outline: 'none',
    minWidth: { sm: 640, xs: 'auto' },
    width: { sm: 640, xs: '100%' },
  },
  paper: {
    borderRadius: { sm: '24px', xs: 0 },
    borderBottomLeftRadius: { sm: 24, xs: 0 },
    borderBottomRightRadius: { sm: 24, xs: 0 },
    position: 'relative',
  },
  contentBox: {
    py: 8,
    px: { sm: 8, xs: 3 },
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 2,
    mt: 3,
  },
  errorText: {
    mt: 1,
    textAlign: 'center',
    display: 'block',
  },
  slideLeft: {
    transform: 'translateX(-20px)',
    opacity: 0,
    transition: 'transform 300ms ease, opacity 300ms ease',
  },
  slideRight: {
    transform: 'translateX(20px)',
    opacity: 0,
    transition: 'transform 300ms ease, opacity 300ms ease',
  },
  slideCenter: {
    transform: 'translateX(0)',
    opacity: 1,
    transition: 'transform 300ms ease, opacity 300ms ease',
  },
};
